import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmarModal from '../components/ConfirmarModal';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Base Fiscal (port do ZADRA). Empresas espelhadas do Acessórias + campos
// manuais (dia de entrega, cidade, IM, IE, responsável) e os acessos a portais.
// Senha e código de acesso nunca vêm na lista: o botão "ver" pede um por vez ao
// servidor, que registra quem viu. Fechar o painel esquece o que foi revelado.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const ACESSO_VAZIO = { titulo: '', usuario: '', link: '', senha: '', codigo: '' };

const SELO_CERT = {
  vencido: ['erro', 'fa-circle-xmark', 'vencido'],
  vence_30d: ['alerta', 'fa-clock', 'vence em breve'],
  arquivo_pendente: ['neutro', 'fa-upload', 'arquivo pendente'],
  sem_validade: ['alerta', 'fa-calendar-xmark', 'sem validade'],
  ok: ['ok', 'fa-circle-check', 'em dia'],
};

const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function SeloCertificado({ cert }) {
  if (!cert) return <span className="fz-selo neutro"><i className="fas fa-minus"></i>sem cadastro</span>;
  const [cls, ico, txt] = SELO_CERT[cert.situacao] || SELO_CERT.sem_validade;
  return <span className={`fz-selo ${cls}`} title={cert.validade ? `validade ${cert.validade}` : undefined}><i className={`fas ${ico}`}></i>{txt}</span>;
}

export default function FiscalBase() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [soMinhas, setSoMinhas] = useState(false);
  const [ocultarInativas, setOcultarInativas] = useState(true);
  const [responsavel, setResponsavel] = useState('');
  const [ordem, setOrdem] = useState('nome');
  const [edicao, setEdicao] = useState(null);        // empresa em edição (campos manuais)
  const [salvando, setSalvando] = useState(false);
  const [painel, setPainel] = useState(null);         // { empresa, acessos }
  const [formAcesso, setFormAcesso] = useState(null); // { id?, ...campos }
  const [revelados, setRevelados] = useState({});     // { `${id}:${campo}`: valor }
  const [excluir, setExcluir] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/empresas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as empresas'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const empresas = useMemo(() => {
    if (!dados) return [];
    const termo = semAcento(busca.trim());
    const digitos = busca.replace(/\D/g, '');
    const lista = dados.empresas.filter((e) => {
      if (ocultarInativas && !e.ativa) return false;
      if (soMinhas && e.responsavel_user_id !== dados.meu_user_id) return false;
      if (responsavel && (e.responsavel || '') !== responsavel) return false;
      if (!termo) return true;
      return semAcento(e.cliente).includes(termo) || String(e.cod) === termo || (digitos.length >= 4 && (e.cnpj || '').includes(digitos));
    });
    return [...lista].sort((a, b) => (ordem === 'entrega'
      ? (a.entrega_dia || 999) - (b.entrega_dia || 999) || (a.cliente || '').localeCompare(b.cliente || '')
      : (a.cliente || '').localeCompare(b.cliente || '')));
  }, [dados, busca, soMinhas, ocultarInativas, responsavel, ordem]);

  const responsaveis = useMemo(() => [...new Set((dados?.empresas || []).map((e) => e.responsavel).filter(Boolean))].sort(), [dados]);

  const sincronizar = () => {
    setSincronizando(true); setMsg(''); setErro('');
    axios.post(`${API}/api/fiscal/acessorias/sincronizar/`, {}, { headers: headers(), timeout: 600000 })
      .then((r) => { setMsg(r.data.mensagem); carregar(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível sincronizar com o Acessórias'))
      .finally(() => setSincronizando(false));
  };

  const trocarEmpresa = (emp) => setDados((d) => ({ ...d, empresas: d.empresas.map((e) => (e.cod === emp.cod ? emp : e)) }));

  const salvarEmpresa = () => {
    setSalvando(true);
    const corpo = { entrega_dia: edicao.entrega_dia, cidade: edicao.cidade, im: edicao.im, ie: edicao.ie };
    if (edicao.responsavel_user_id !== edicao._resp_original) corpo.responsavel_user_id = edicao.responsavel_user_id;
    axios.patch(`${API}/api/fiscal/empresas/${edicao.cod}/`, corpo, { headers: headers() })
      .then((r) => { trocarEmpresa(r.data.empresa); setMsg(r.data.mensagem); setEdicao(null); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvando(false));
  };

  // ---- acessos ----
  const abrirAcessos = (empresa) => {
    setRevelados({}); setFormAcesso(null);
    setPainel({ empresa, acessos: null });
    axios.get(`${API}/api/fiscal/empresas/${empresa.cod}/acessos/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setPainel({ empresa, acessos: r.data.acessos }))
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível carregar os acessos'); setPainel(null); });
  };
  const fecharAcessos = () => { setPainel(null); setRevelados({}); setFormAcesso(null); };
  const aplicarAcessos = (r) => {
    const empresa = { ...painel.empresa, acessos: r.data.acessos.length };
    setPainel({ empresa, acessos: r.data.acessos });
    trocarEmpresa(empresa);
  };
  const salvarAcesso = () => {
    setSalvando(true);
    const { id, _tem_senha, _tem_codigo, ...corpo } = formAcesso;
    const req = id
      ? axios.put(`${API}/api/fiscal/acessos/${id}/`, corpo, { headers: headers() })
      : axios.post(`${API}/api/fiscal/empresas/${painel.empresa.cod}/acessos/`, corpo, { headers: headers() });
    req.then((r) => { aplicarAcessos(r); setFormAcesso(null); setRevelados({}); setMsg(r.data.mensagem); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar o acesso'))
      .finally(() => setSalvando(false));
  };
  const confirmarExclusao = () => {
    setSalvando(true);
    axios.delete(`${API}/api/fiscal/acessos/${excluir.id}/`, { headers: headers() })
      .then((r) => { aplicarAcessos(r); setMsg(r.data.mensagem); setExcluir(null); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível excluir'); setExcluir(null); })
      .finally(() => setSalvando(false));
  };
  const revelar = (id, campo) => {
    const chave = `${id}:${campo}`;
    if (revelados[chave] !== undefined) { setRevelados(({ [chave]: _, ...resto }) => resto); return; }
    axios.post(`${API}/api/fiscal/acessos/${id}/revelar/`, { campo }, { headers: headers() })
      .then((r) => setRevelados((rv) => ({ ...rv, [chave]: r.data.valor })))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível mostrar'));
  };
  const copiar = (texto, rotulo) => {
    navigator.clipboard.writeText(texto).then(() => setMsg(`${rotulo} copiado`)).catch(() => setErro('Não foi possível copiar'));
  };
  const copiarSegredo = (id, campo, rotulo) => {
    const chave = `${id}:${campo}`;
    if (revelados[chave] !== undefined) { copiar(revelados[chave], rotulo); return; }
    axios.post(`${API}/api/fiscal/acessos/${id}/revelar/`, { campo }, { headers: headers() })
      .then((r) => copiar(r.data.valor, rotulo))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível copiar'));
  };

  const linhaSegredo = (a, campo, rotulo, tem) => {
    if (!tem) return null;
    const valor = revelados[`${a.id}:${campo}`];
    return (
      <div className="fz-campo">
        <span className="rot">{rotulo}</span>
        <span className={`val fz-mono ${valor === undefined ? 'fz-oculto' : ''}`}>{valor === undefined ? '••••••••' : valor}</span>
        <span className="d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary" title={valor === undefined ? 'Mostrar' : 'Ocultar'} onClick={() => revelar(a.id, campo)}>
            <i className={`fas ${valor === undefined ? 'fa-eye' : 'fa-eye-slash'}`}></i>
          </button>
          <button className="btn btn-sm btn-outline-secondary" title="Copiar" onClick={() => copiarSegredo(a.id, campo, rotulo)}><i className="fas fa-copy"></i></button>
        </span>
      </div>
    );
  };

  const total = dados?.empresas?.length || 0;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-building-columns header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Base Fiscal</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            <button type="button" className="gr-btn primaria" onClick={sincronizar} disabled={sincronizando}>
              <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'}`} aria-hidden="true"></i>
              {sincronizando ? 'Sincronizando...' : 'Sincronizar com Acessórias'}
            </button>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Cadastro, regime, situação e responsável vêm do <b>Acessórias</b> (botão Sincronizar). Aqui você ajusta só o <b>dia de entrega</b>,
          <b> cidade</b>, <b>IM</b>, <b>IE</b> e o <b>responsável no Saber</b>. Empresa <b>ativa</b> = ativa no Acessórias e com responsável fiscal.
          Senhas dos portais ficam cifradas; cada vez que alguém clica em mostrar ou copiar, fica registrado.
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="fz-filtros">
          <input className="po-busca" placeholder="Buscar por nome, código ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="fz-sel" value={ordem} onChange={(e) => setOrdem(e.target.value)} aria-label="Ordenar">
            <option value="nome">Ordenar A–Z</option>
            <option value="entrega">Ordenar por dia de entrega</option>
          </select>
          <label className="po-toggle"><input type="checkbox" checked={soMinhas} onChange={(e) => setSoMinhas(e.target.checked)} />Só as minhas</label>
          <label className="po-toggle"><input type="checkbox" checked={ocultarInativas} onChange={(e) => setOcultarInativas(e.target.checked)} />Ocultar inativas</label>
        </div>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando empresas...</div></div>
        ) : (
          <section className="po-card po-enter">
            <header className="po-card-head">
              <h3><i className="fas fa-building" aria-hidden="true"></i>Empresas</h3>
              <span className="badge bg-light text-dark">{empresas.length} de {total}</span>
            </header>
            <div className="po-card-body">
              <div className="po-scroll" style={{ maxHeight: 'none', overflow: 'visible' }}>
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Cód.</th><th>Empresa</th><th>Regime</th><th>Responsável</th><th className="text-center">Entrega</th><th>Cidade</th><th>IM / IE</th><th>Certificado</th><th className="text-center">Acessos</th><th></th></tr></thead>
                  <tbody>
                    {empresas.map((e) => (
                      <tr key={e.cod} style={e.ativa ? undefined : { opacity: .6 }}>
                        <td className="fz-num">{e.cod}</td>
                        <td>
                          <div className="fw-semibold">{e.cliente}</div>
                          <div className="fz-sub fz-mono">{fmtCnpj(e.cnpj)}{!e.ativa && <span className="ms-2 badge bg-secondary">{e.status === 'Ativa' ? 'sem responsável' : (e.status || 'inativa').toLowerCase()}</span>}</div>
                        </td>
                        <td className="small">{e.tributacao || '—'}</td>
                        <td className="small">
                          {e.responsavel || <span className="text-muted">—</span>}
                          {e.responsavel && !e.responsavel_login && <div className="fz-sub text-warning" title="Nenhum login do Saber vinculado — o filtro 'Só as minhas' não funciona para esta pessoa"><i className="fas fa-link-slash me-1"></i>sem login</div>}
                        </td>
                        <td className="text-center fz-num">{e.entrega_dia || <span className="text-muted">—</span>}</td>
                        <td className="small">{e.cidade ? `${e.cidade}${e.uf ? `/${e.uf}` : ''}` : <span className="text-muted">—</span>}</td>
                        <td className="small fz-mono">{e.im || '—'}<div className="fz-sub">{e.ie || ''}</div></td>
                        <td><SeloCertificado cert={e.certificado} /></td>
                        <td className="text-center fz-num">{e.acessos}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-outline-primary me-1" title="Acessos aos portais" onClick={() => abrirAcessos(e)}><i className="fas fa-key"></i></button>
                          <button className="btn btn-sm btn-outline-secondary" title="Editar dados manuais"
                            onClick={() => setEdicao({ ...e, entrega_dia: e.entrega_dia || '', cidade: e.cidade || '', im: e.im || '', ie: e.ie || '', _resp_original: e.responsavel_user_id })}>
                            <i className="fas fa-pen"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {empresas.length === 0 && <tr><td colSpan={10} className="text-center text-muted py-4">Nenhuma empresa com esses filtros.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {edicao && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-pen me-2" style={{ color: '#00b5cc' }}></i>Editar empresa</h5>
                    <small style={{ color: '#6c757d' }}>{edicao.cod} · {edicao.cliente}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setEdicao(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Dia de entrega</label>
                      <input type="number" min="1" max="31" className="form-control" value={edicao.entrega_dia} onChange={(ev) => setEdicao((f) => ({ ...f, entrega_dia: ev.target.value }))} />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label small fw-bold mb-1">Cidade</label>
                      <input className="form-control" value={edicao.cidade} onChange={(ev) => setEdicao((f) => ({ ...f, cidade: ev.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Inscrição municipal</label>
                      <input className="form-control" value={edicao.im} onChange={(ev) => setEdicao((f) => ({ ...f, im: ev.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Inscrição estadual</label>
                      <input className="form-control" value={edicao.ie} onChange={(ev) => setEdicao((f) => ({ ...f, ie: ev.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Responsável no Saber</label>
                      <select className="form-select" value={edicao.responsavel_user_id || ''}
                        onChange={(ev) => setEdicao((f) => ({ ...f, responsavel_user_id: ev.target.value ? Number(ev.target.value) : null }))}>
                        <option value="">— sem vínculo —</option>
                        {(dados?.usuarios || []).map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
                      </select>
                      <div className="form-text">No Acessórias: {edicao.responsavel || '—'}. Ajustar aqui vale para o filtro "Só as minhas" e não é desfeito pelo sincronismo.</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setEdicao(null)}><i className="fas fa-times me-2"></i>Cancelar</button>
                  <button className="btn btn-primary" onClick={salvarEmpresa} disabled={salvando}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{salvando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {painel && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-key me-2" style={{ color: '#00b5cc' }}></i>Acessos aos portais</h5>
                    <small style={{ color: '#6c757d' }}>{painel.empresa.cod} · {painel.empresa.cliente}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={fecharAcessos}></button>
                </div>
                <div className="modal-body">
                  {formAcesso ? (
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Título</label>
                        <input className="form-control" placeholder="e-CAC, Prefeitura, Sefaz..." value={formAcesso.titulo} onChange={(ev) => setFormAcesso((f) => ({ ...f, titulo: ev.target.value }))} autoFocus />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Usuário</label>
                        <input className="form-control" autoComplete="off" value={formAcesso.usuario} onChange={(ev) => setFormAcesso((f) => ({ ...f, usuario: ev.target.value }))} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Senha</label>
                        <input type="password" className="form-control" autoComplete="new-password" placeholder={formAcesso.id ? (formAcesso._tem_senha ? 'em branco = manter a atual' : 'sem senha cadastrada') : ''}
                          value={formAcesso.senha} onChange={(ev) => setFormAcesso((f) => ({ ...f, senha: ev.target.value }))} />
                        {formAcesso.id && formAcesso._tem_senha && (
                          <label className="form-check-label small mt-1"><input type="checkbox" className="form-check-input me-1" checked={!!formAcesso.limpar_senha} onChange={(ev) => setFormAcesso((f) => ({ ...f, limpar_senha: ev.target.checked }))} />apagar a senha cadastrada</label>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Código de acesso</label>
                        <input type="password" className="form-control" autoComplete="new-password" placeholder={formAcesso.id ? (formAcesso._tem_codigo ? 'em branco = manter o atual' : 'sem código cadastrado') : ''}
                          value={formAcesso.codigo} onChange={(ev) => setFormAcesso((f) => ({ ...f, codigo: ev.target.value }))} />
                        {formAcesso.id && formAcesso._tem_codigo && (
                          <label className="form-check-label small mt-1"><input type="checkbox" className="form-check-input me-1" checked={!!formAcesso.limpar_codigo} onChange={(ev) => setFormAcesso((f) => ({ ...f, limpar_codigo: ev.target.checked }))} />apagar o código cadastrado</label>
                        )}
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-bold mb-1">Link</label>
                        <input className="form-control" placeholder="https://..." value={formAcesso.link} onChange={(ev) => setFormAcesso((f) => ({ ...f, link: ev.target.value }))} />
                      </div>
                    </div>
                  ) : painel.acessos === null ? (
                    <div className="text-center py-4 text-muted"><i className="fas fa-spinner fa-spin me-2"></i>Carregando acessos...</div>
                  ) : painel.acessos.length === 0 ? (
                    <div className="text-center py-4 text-muted">Nenhum acesso cadastrado para esta empresa.</div>
                  ) : painel.acessos.map((a) => (
                    <div className="fz-acesso" key={a.id}>
                      <div className="fz-acesso-topo">
                        <h6>{a.titulo}</h6>
                        {a.link && <a className="btn btn-sm btn-outline-primary" href={a.link} target="_blank" rel="noopener noreferrer" title="Abrir o portal"><i className="fas fa-arrow-up-right-from-square me-1"></i>abrir</a>}
                        <span className="ms-auto d-flex gap-1">
                          <button className="btn btn-sm btn-outline-secondary" title="Editar"
                            onClick={() => setFormAcesso({ id: a.id, titulo: a.titulo || '', usuario: a.usuario || '', link: a.link || '', senha: '', codigo: '', _tem_senha: a.tem_senha, _tem_codigo: a.tem_codigo })}>
                            <i className="fas fa-pen"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => setExcluir(a)}><i className="fas fa-trash"></i></button>
                        </span>
                      </div>
                      {a.usuario && (
                        <div className="fz-campo">
                          <span className="rot">Usuário</span><span className="val fz-mono">{a.usuario}</span>
                          <button className="btn btn-sm btn-outline-secondary" title="Copiar" onClick={() => copiar(a.usuario, 'Usuário')}><i className="fas fa-copy"></i></button>
                        </div>
                      )}
                      {linhaSegredo(a, 'senha', 'Senha', a.tem_senha)}
                      {linhaSegredo(a, 'codigo', 'Código', a.tem_codigo)}
                    </div>
                  ))}
                </div>
                <div className="modal-footer bg-light">
                  {formAcesso ? (
                    <>
                      <button className="btn btn-secondary me-auto" onClick={() => setFormAcesso(null)}><i className="fas fa-arrow-left me-2"></i>Voltar</button>
                      <button className="btn btn-primary" onClick={salvarAcesso} disabled={salvando || !formAcesso.titulo.trim()}>
                        <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{salvando ? 'Salvando...' : 'Salvar acesso'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-secondary me-auto" onClick={fecharAcessos}><i className="fas fa-times me-2"></i>Fechar</button>
                      <button className="btn btn-primary" onClick={() => setFormAcesso({ ...ACESSO_VAZIO })}><i className="fas fa-plus me-2"></i>Novo acesso</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmarModal aberto={!!excluir} perigo titulo="Excluir acesso" rotuloConfirmar="Excluir" processando={salvando}
          texto={excluir && <>Excluir o acesso <b>{excluir.titulo}</b> de {painel?.empresa?.cliente}? A senha cadastrada é apagada junto e não dá para recuperar.</>}
          onConfirmar={confirmarExclusao} onCancelar={() => setExcluir(null)} />
      </div>
    </div>
  );
}
